
import BaseTable from 'components/Tables/BaseTable';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import dayjs from 'dayjs';
import GroupDualStatus from 'features/Integrations/components/GroupDualStatus';
import { groupSearchFilter } from 'features/Integrations/utils/integrationSearchUtils';
import { useCallback, useMemo, useState } from 'react';
import { FaCog } from 'react-icons/fa';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { useGetGroupsQuery } from 'services/devices/groups/groups.api';
import { getDeviceIconForGroup } from 'utils/devices/icon.utils';
import '../assets/connectedDeviceGroups.css';
import { DeviceRow } from './DeviceRows';
import BaseEllipsisMenu from 'components/BaseEllipsisMenu/BaseEllipsisMenu';

export default function Table({
  hasCreateEditPermission,
  hasDeletePermission,
  onEditGroup,
  onDeleteGroup,
  onOpenGroupSettings,
}) {
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
    isError: connectedDevicesError,
  } = useGetConnectedDevicesQuery();
  const { data: deviceGroups = [], isFetching: deviceGroupsFetching } = useGetGroupsQuery();
  const [ellipsisOpenGroupId, setEllipsisOpenGroupId] = useState(null);
  const cellWrapperClass = 'd-flex align-items-center gap-2 flex-wrap';

  const rows = useMemo(() => {
    return (
      deviceGroups
        ?.filter((group) => groupSearchFilter(group, connectedDevices))
        ?.map((group) => ({
          ...group,
          id: group.id,
          groupStatus: group, // Pass the group object for GroupDualStatus
          devicesCount: group.devices?.length ?? 0,
          dateCreatedFormatted: dayjs(group?.date_created).format('MM/DD/YYYY, hh:mm:ss A'),
        })) ?? []
    );
  }, [deviceGroups, connectedDevices, connectedDevicesLoading]);

  // Columns drive the BaseTable plus detail panel dropdown per group.
  const columns = useMemo(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        Cell: ({ cell }) => (
          <div className={cellWrapperClass}>
            <span className="text-break">{cell.getValue()}</span>
          </div>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
        Cell: ({ row }) => {
          const group = row.original;
          return (
            <div className={`${cellWrapperClass} w-100`}>
              <img
                className="d-none d-md-block"
                src={getDeviceIconForGroup(group, connectedDevices)}
                alt={group?.type || 'RescAlert'}
                width={'30px'}
                height={'50px'}
                style={{ objectFit: 'contain' }}
              />
              <p className="m-0 text-truncate text-break">{group?.name}</p>
            </div>
          );
        },
      },
      {
        accessorKey: 'devicesCount',
        header: 'Device(s)',
        Cell: ({ cell }) => (
          <div className={cellWrapperClass}>
            <span>{cell.getValue()}</span>
          </div>
        ),
      },
      {
        accessorKey: 'dateCreatedFormatted',
        header: 'Date Created',
        Cell: ({ cell }) => (
          <div className={cellWrapperClass}>
            <span className="text-break">{cell.getValue()}</span>
          </div>
        ),
      },
      {
        accessorKey: 'groupStatus',
        header: 'STATUS',
        minSize: 180, // Prevent shrinking
        size: 200, // Give a reasonable default width
        grow: false, // Prevent column from shrinking
        Cell: ({ cell }) => (
          <div className="d-flex align-items-center gap-2" style={{ minWidth: 160 }}>
            <GroupDualStatus group={cell.getValue()} />
          </div>
        ),
      },
      {
        id: 'Actions',
        header: '',
        size: 72,
        minSize: 60,
        maxSize: 120,
        grow: false,
        Cell: ({ row }) => {
          const group = row.original;

          return (
            <div className="d-flex align-items-center justify-content-end gap-3 w-100 pe-1">
              {hasCreateEditPermission && (
                <button
                  type="button"
                  className="btn btn-link p-0 text-body"
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenGroupSettings?.(group?.id);
                  }}
                >
                  <FaCog size={24} />
                </button>
              )}
              {(hasCreateEditPermission || hasDeletePermission) && (
                <BaseEllipsisMenu
                  open={ellipsisOpenGroupId === group?.id}
                  onOpen={() => setEllipsisOpenGroupId(group?.id)}
                  onClose={() => setEllipsisOpenGroupId(null)}
                >
                  {hasCreateEditPermission && (
                    <button
                      className="btn btn-primary w-100 text-center"
                      onClick={(event) => {
                        event.stopPropagation();
                        setEllipsisOpenGroupId(null);
                        onEditGroup(group?.id);
                      }}
                    >
                      Edit
                    </button>
                  )}
                  {hasDeletePermission && (
                    <button
                      className="btn btn-danger w-100 text-center"
                      onClick={(event) => {
                        event.stopPropagation();
                        setEllipsisOpenGroupId(null);
                        onDeleteGroup(group);
                      }}
                    >
                      Remove
                    </button>
                  )}
                </BaseEllipsisMenu>
              )}
              {row.getIsExpanded() ? <MdExpandLess size={20} /> : <MdExpandMore size={20} />}
            </div>
          );
        },
      },
    ],
    [
      hasCreateEditPermission,
      hasDeletePermission,
      onDeleteGroup,
      onEditGroup,
      onOpenGroupSettings,
      ellipsisOpenGroupId,
      connectedDevices,
      connectedDevicesLoading,
    ],
  );

  const dropdownMenu = (group) => {
    if (connectedDevicesLoading) {
      return (
        <div className="d-flex justify-content-center align-items-center py-3 text-secondary">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          Loading devices...
        </div>
      );
    }

    if (connectedDevicesError) {
      return <div className="p-2 text-center text-danger">Unable to load devices.</div>;
    }

    if (!group?.devices?.length)
      return <div className="p-2 text-center text-muted">No devices in this group.</div>;

    return (
      <div className="connected-device-groups-detail w-100 px-5">
        <div className="row px-2 py-1 border-bottom border-secondary fw-bold">
          <div className="col-12 col-md-4 col-lg-3">Device</div>
          <div className="col-12 col-md-3 col-lg-2">Available Licenses</div>
          <div className="col-12 col-md-2 col-lg-2">Status</div>
          <div className="col-12 col-md-2 col-lg-3">Date Created</div>
          <div className="col-12 col-md-1 col-lg-2" />
        </div>
        {group.devices.map((deviceId, idx) => (
          <DeviceRow
            key={`${group.id}-${deviceId}-${idx}`}
            deviceId={deviceId}
            last={idx === group.devices.length - 1}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="mt-3 d-none d-lg-block">
      {!deviceGroupsFetching ? (
        <BaseTable
          columns={columns}
          rows={rows}
          expandable
          tablePreferenceKey="connected-device-groups"
          // conditionally disable row virtualization for better dropdown performance
          enableRowVirtualization={rows.length > 50}
          initialState={{
            columnVisibility: {
              licenses: false,
              status: false,
            },
          }}
          customStyles={{
            cell: {
              cursor: 'pointer',
              padding: '12px 16px',
            },
            header: { padding: '12px 16px' },
          }}
          dropdownMenu={dropdownMenu}
          customEmptyMessage="No Groups to Display"
        />
      ) : (
        <WidgetLoadingAnimation />
      )}
    </div>
  );
}
